import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import cn from "classnames";
import style from "./style.module.scss";
import { Order } from "../../../services";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { requestErrorDisplay } from "../../../utils";
import OrderItemFilter from "../section-item/components/OrderItemFilter";
import ExportOrders from "../section-item/components/ExportOrders";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { CLIENT_TABS } from "../../../mock";
import FilterIcon from "../../shared/icons/FilterIcon";
import RefreshIcon from "../../shared/icons/RefreshIcon";
import { XIcon } from "lucide-react";
import { withTenderStatusFilter } from "../../../utils/orderTenderFilter";
import { TRANSPORTATION_TYPES } from "../../../pages/orders/utils/railwayOrder";

const OrdersTab = ({
  transportationType = TRANSPORTATION_TYPES.ROAD,
  setStatus,
  changeSortColumn,
  sortColumn = null,
  onDownloadExcel,
  isCallTaps = false,
  countryFrom,
  countryTo,
  cityFrom,
  cityTo,
  loadingDate,
  creationDate,
  selectedStatus,
  selectedCurrency,
  senderName,
  receiverName,
  orderNo,
  executor,
  selectedEmployee,
  statusActive,
  onToggleFilter = () => {},
  isFilterIcon = false,
  onOpenFilter = () => {},
  isFilterOpen = false,
  onReload,
  onReset,
}) => {
  const [orderCounts, setOrderCounts] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [activeStatus, setActiveStatus] = useState(() => {
    const defaultTab = CLIENT_TABS((t) => t).find((tab) => tab.key === "all");
    return defaultTab || CLIENT_TABS((t) => t)[0];
  });
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const dropdownContent = useRef(null);
  const { t } = useTranslation();

  useClickOutside(dropdownContent, () => setDropdownOpen(false));

  const items = useMemo(() => {
    return CLIENT_TABS(t).map((tab) => ({
      key: tab.key,
      statuses: tab.statuses,
      title: tab.title,
      isExpiredTender: tab.isExpiredTender,
      quantity: orderCounts[tab.key] || 0,
    }));
  }, [orderCounts, t]);

  const updateActiveStatus = (item) => {
    setActiveStatus(item);
    setStatus(item);
  };

  useEffect(() => {
    if (statusActive) {
      setActiveStatus(statusActive);
    }
  }, [statusActive]);

  const fetchData = useCallback(async () => {
    setLoading(true);

    const statusGroups = CLIENT_TABS(t).map((tab) => ({
      key: tab.key,
      status: tab.key === "all" ? [] : tab.statuses,
    }));

    const statuses =
      Array.isArray(selectedStatus) && selectedStatus.length > 0
        ? selectedStatus.map((s) => s.value)
        : [];

    const params = {
      transportationType,
      statusGroups,
      statuses,
      countryIdFrom: countryFrom ? countryFrom.id : "",
      cityIdFrom: cityFrom ? cityFrom.id : "",
      countryIdTo: countryTo ? countryTo.id : "",
      cityIdTo: cityTo ? cityTo.id : "",
      startShippingDate:
        Array.isArray(loadingDate) && loadingDate[0] ? moment(loadingDate[0]).format("YYYY-MM-DD") : "",
      endShippingDate:
        Array.isArray(loadingDate) && loadingDate[1] ? moment(loadingDate[1]).format("YYYY-MM-DD") : "",
      startCreatedAt:
        Array.isArray(creationDate) && creationDate[0] ? moment(creationDate[0]).format("YYYY-MM-DD") : "",
      endCreatedAt:
        Array.isArray(creationDate) && creationDate[1] ? moment(creationDate[1]).format("YYYY-MM-DD") : "",
      currencies: selectedCurrency ? [selectedCurrency.value] : [],
      orderNo: String(orderNo || ""),
      senderName: senderName || "",
      recipientName: receiverName || "",
      executorName: executor ? executor.companyName : "",
      userId: selectedEmployee ? selectedEmployee.id : "",
    };

    const requestParams = withTenderStatusFilter(params);

    if (statusActive?.key !== "all") {
      delete requestParams.isExpiredTender;

      if (typeof statusActive?.isExpiredTender === "boolean") {
        requestParams.isExpiredTender = statusActive.isExpiredTender;
      }
    }

    try {
      const response = await Order.getOwnOrderQuantityNew(requestParams);
      if (response?.data) {
        const counts = response.data.reduce((acc, item) => {
          acc[item.key] = item.count;
          return acc;
        }, {});
        setOrderCounts(counts);

        if (!statusActive) {
          const defaultTab = CLIENT_TABS(t).find((tab) => tab.key === "all");
          if (defaultTab) {
            updateActiveStatus(defaultTab);
          }
        }
      }
    } catch (e) {
      requestErrorDisplay(e);
    } finally {
      setLoading(false);
    }
  }, [transportationType, countryFrom, countryTo, cityFrom, cityTo, loadingDate, creationDate, selectedStatus, selectedCurrency, senderName, receiverName, orderNo, executor, selectedEmployee, statusActive, t]);

  useEffect(() => {
    fetchData();
  }, [transportationType, isCallTaps, countryFrom, countryTo, cityFrom, cityTo, loadingDate, creationDate, selectedStatus, selectedCurrency, senderName, receiverName, orderNo, executor, selectedEmployee, statusActive]);

  const toggleDropdown = () => {
    setDropdownOpen(!isDropdownOpen);
  };

  const handleReload = () => {
    if (onReload) {
      onReload();
    }
  };

  const hasActiveFilters = useMemo(() => {
    return !!(
      countryFrom ||
      countryTo ||
      cityFrom ||
      cityTo ||
      (Array.isArray(loadingDate) && loadingDate.length > 0 && loadingDate[0]) ||
      (Array.isArray(creationDate) && creationDate.length > 0 && creationDate[0]) ||
      (Array.isArray(selectedStatus) && selectedStatus.length > 0 && selectedStatus[0]) ||
      selectedCurrency ||
      senderName ||
      receiverName ||
      orderNo ||
      executor ||
      selectedEmployee
    );
  }, [
    countryFrom,
    countryTo,
    cityFrom,
    cityTo,
    loadingDate,
    creationDate,
    selectedStatus,
    selectedCurrency,
    senderName,
    receiverName,
    orderNo,
    executor,
    selectedEmployee,
  ]);

  return (
    <div className={cn(style.wrapper)}>
      {/* Dropdown отображается только для мобильной версии */}
      <div ref={dropdownContent} className={style.dropdownWrapper}>
        <div className={style.flex}>
          <button
            type="button"
            className={cn(style.button, "d-flex align-items-center", {
              [style.button_active]: activeStatus?.key,
            })}
            onClick={toggleDropdown}
          >
            {activeStatus?.title}
            <span className={cn(style.count)}>
              {activeStatus?.quantity || orderCounts[activeStatus?.key] || 0}
            </span>
            <div className={style.imgSelect}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="7"
                viewBox="0 0 10 7"
                fill="none"
                className={style.checkmark}
              >
                <path
                  d="M1 1.5L5 5.5L9 1.5"
                  stroke="#909195"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>
        </div>
        {isDropdownOpen && (
          <div className={style.dropdownContent}>
            {items.map((item) => (
              <button
                type="button"
                key={"dropdown-" + item.key}
                className={cn(style.button, "d-flex align-items-center", {
                  [style.button_none]: item.key === activeStatus?.key,
                })}
                onClick={() => {
                  toggleDropdown();
                  updateActiveStatus(item);
                }}
              >
                {item.title}
                <span className={style.count}>{item.quantity}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Код для не мобильной версии */}
      <div className={style.mobile}>
        {items.map((item) => (
          <button
            type="button"
            key={"desktop-" + item.key}
            className={cn(style.button, "d-flex align-items-center text-nowrap", {
              [style.button_active]: item.key === activeStatus?.key,
            })}
            onClick={() => updateActiveStatus(item)}
          >
            {item.title}
            <span
              className={cn(style.count, {
                [style.count_active]: item.key === activeStatus?.key,
              })}
            >
              {item.quantity}
            </span>
          </button>
        ))}
      </div>

      {/* {!isFilterIcon && (
        <button
          type="button"
          className={cn(style.filterBtn)}
          onClick={handleReload}
          title={t("orderFilter.refresh")}
        >
          <RefreshIcon />
          {t("orderFilter.refresh")}
        </button>
      )}
      {isFilterIcon && <RefreshIcon onClick={handleReload} className={style.mobileFilterIcon} />} */}

      {/* <OrderItemFilter changeSortColumn={changeSortColumn} sortColumn={sortColumn} /> */}

      <div className={cn(style.filterBtn, { [style.filterBtn_active]: isFilterOpen })}>
        <button
          type="button"
          className={style.innerBtn}
          onClick={onOpenFilter}
          title={t("orderFilter.title")}
        >
          <FilterIcon />
          {t("orderFilter.title")}
        </button>

        {onReset && hasActiveFilters && (
          <button
            type="button"
            className={cn(style.innerBtn, style.resetBtn)}
            onClick={onReset}
            title={t("orderFilter.resetButton")}
          >
            <XIcon size={20} />
          </button>
        )}
      </div>

      <ExportOrders onDownloadExcel={onDownloadExcel} />
    </div>
  );
};

export default OrdersTab;
